import { useLocation, Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Calendar, Euro, Mail, Phone, ArrowRight } from 'lucide-react'
import { useQuery } from 'react-query'
import { apiService as api } from '../services/api'
import confirmationImage from '../assets/confirmation.png'

const Confirmation = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { reservation: stateReservation, client, produit } = location.state || {}
  
  // Récupérer l'ID de réservation depuis l'URL ou le state
  const reservationId = searchParams.get('id') || stateReservation?.id

  // Charger la réservation si elle n'est pas dans le state
  const { data: reservationData, isLoading: reservationLoading } = useQuery(
    ['reservation', reservationId],
    () => api.get(`/reservations/${reservationId}`).then(res => res.data),
    {
      enabled: !!reservationId && !stateReservation,
      staleTime: 5 * 60 * 1000
    }
  )

  // Charger les paramètres admin
  const { data: adminSettingsData } = useQuery(
    'rentman-settings-public',
    () => api.get('/rentman/settings/public', { params: { t: Date.now() } }).then(res => res.data),
    {
      staleTime: 5 * 60 * 1000
    }
  )
  const backendShowPrice = adminSettingsData?.show_regular_price
  const showPrice = backendShowPrice === true

  // Utiliser la réservation du state ou celle chargée via l'API
  const reservation = stateReservation || reservationData?.reservation

  // Affichage du chargement
  if (reservationLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement de la réservation...</p>
        </div>
      </div>
    )
  }

  // Affichage si aucune réservation trouvée
  if (!reservation) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Réservation non trouvée</h2>
          <p className="text-gray-400 mb-6">
            {reservationId 
              ? `Aucune réservation trouvée avec l'ID ${reservationId}`
              : 'Cette page n\'est accessible qu\'après une réservation ou avec un ID de réservation.'
            }
          </p>
          <div className="space-y-2">
            <Link to="/catalogue-materiels" className="btn-primary block">
              Retour au catalogue
            </Link>
            <p className="text-sm text-gray-500">
              Vous pouvez aussi accéder à cette page avec : /confirmation?id=123
            </p>
          </div>
        </div>
      </div>
    )
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  // Correction: +1 pour compter le jour de fin inclus
  const days = Math.ceil((new Date(reservation.end_date) - new Date(reservation.start_date)) / (1000 * 60 * 60 * 24)) + 1

  return (
    <div className="min-h-screen bg-black">
      <div className="container-custom py-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          <img 
            src={confirmationImage} 
            alt="Réservation confirmée" 
            className="h-24 w-auto mx-auto mb-6"
          />
        <h1 className="text-4xl font-bold mb-4">
            <span className="text-gradient">Réservation confirmée !</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Votre réservation a été enregistrée avec succès. Un email de confirmation vous a été envoyé.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Reservation Details */}
          <div className="card">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <Calendar className="h-6 w-6 text-primary-500 mr-3" />
              Détails de la réservation
            </h2>

            <div className="space-y-6">
              {/* Product Info */}
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 bg-dark-700 rounded-lg overflow-hidden flex-shrink-0">
                  {produit?.image_url ? (
                    <img
                      src={produit.image_url}
                      alt={produit.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <Camera className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-1">{produit?.name}</h3>
                  <p className="text-gray-400 text-sm">{produit?.category_name}</p>
                  {showPrice && (
                    <p className="text-primary-500 font-semibold">
                      {formatPrice(produit?.price_per_day)}/jour
                    </p>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-dark-700">
                  <span className="text-gray-400">Date de début</span>
                  <span className="font-medium">{formatDate(reservation.start_date)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-dark-700">
                  <span className="text-gray-400">Date de fin</span>
                  <span className="font-medium">{formatDate(reservation.end_date)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-dark-700">
                  <span className="text-gray-400">Durée</span>
                  <span className="font-medium">{days} jour{days > 1 ? 's' : ''}</span>
                </div>
                {showPrice && (
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-400">Total</span>
                    <span className="text-2xl font-bold text-primary-500">
                      {formatPrice(reservation.total_price)}
                    </span>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                  <span className="text-yellow-400 font-medium">En attente de confirmation</span>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Nous vous contacterons sous 24h pour confirmer la disponibilité du matériel.
                </p>
              </div>
            </div>
          </div>

          {/* Client Information */}
          <div className="card">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <User className="h-6 w-6 text-primary-500 mr-3" />
              Informations client
            </h2>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium">{client?.first_name} {client?.last_name}</p>
                  <p className="text-sm text-gray-400">Nom complet</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium">{client?.email}</p>
                  <p className="text-sm text-gray-400">Email de contact</p>
                </div>
              </div>

              {client?.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium">{client.phone}</p>
                    <p className="text-sm text-gray-400">Téléphone</p>
                  </div>
                </div>
              )}

              {client?.company && (
                <div className="flex items-center space-x-3">
                  <Building className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium">{client.company}</p>
                    <p className="text-sm text-gray-400">Entreprise</p>
                  </div>
                </div>
              )}
            </div>

            {/* Reservation ID */}
            <div className="mt-6 pt-6 border-t border-dark-700">
              <p className="text-sm text-gray-400 mb-2">Numéro de réservation</p>
              <p className="text-lg font-mono font-semibold text-primary-500">
                #{reservation.id}
              </p>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="card">
            <h2 className="text-2xl font-semibold mb-6 text-center">Prochaines étapes</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="font-semibold mb-2">1. Email de confirmation</h3>
                <p className="text-sm text-gray-400">
                  Un email détaillé vous a été envoyé avec tous les détails de votre réservation.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="font-semibold mb-2">2. Confirmation téléphonique</h3>
                <p className="text-sm text-gray-400">
                  Notre équipe vous contactera sous 24h pour confirmer la disponibilité.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="font-semibold mb-2">3. Récupération</h3>
                <p className="text-sm text-gray-400">
                  Récupérez votre matériel à la date de début de location.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="text-center mt-12">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/catalogue-materiels"
              className="btn-primary inline-flex items-center justify-center group"
            >
              Nouvelle réservation
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/"
              className="btn-secondary inline-flex items-center justify-center"
            >
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Confirmation
